import { useState } from 'react';
import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Loading } from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import type { Market, SuspensionReasonCode } from '../dataProvider/types';
import { SuspensionRow } from './SuspensionRow';
import { useSuspensions } from './useSuspensions';
import type { SuspensionRow as Row } from './suspensionsModel';
import { REASON_CODE_OPTIONS } from './suspensionState';

/**
 * Suspended-users worklist (D-suspension-flow, Phase 5 surface 2). It makes the
 * suspension LIFECYCLE first-class on top of the fraud foundation: structured
 * reason codes, an admin-chosen period, a derived elapsed-pending-lift state off
 * the injected clock, and a recorded appeal path. A filtered view over users keyed
 * on a held account (not the risk-ranked queue), organised as a worklist:
 * elapsed-pending-lift on top, pending appeals as their own section, running
 * suspensions below.
 *
 * INVESTIGATIVE-GLOBAL: it does NOT read the region switcher (a held account can
 * sit in either market). It is global by default; the region facet filters the
 * user's market by exact value, not through marketsForScope.
 *
 * SENSITIVE: gated by ROLE, not capability (like the fraud queue and the audit
 * log), so a viewer's read-everywhere wildcard cannot reach it. Only the fraud
 * reviewer and super_admin see it.
 */

const Section = ({
  title,
  rows,
  emptyText,
  testId,
}: {
  title: string;
  rows: Row[];
  emptyText: string;
  testId: string;
}) => (
  <Box sx={{ mb: 4 }} data-testid={testId}>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>
      {title} ({rows.length})
    </Typography>
    {rows.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    ) : (
      rows.map((row) => <SuspensionRow key={row.id} row={row} />)
    )}
  </Box>
);

/** The surface without the access wrapper. Exported so tests can inject `asOf`
 *  (the clock the elapsed state is measured from). */
export const SuspensionsView = ({ asOf }: { asOf?: number }) => {
  const [now] = useState<number>(() => asOf ?? Date.now());
  const [regionFilter, setRegionFilter] = useState<Market | null>(null);
  const [reasonFilter, setReasonFilter] = useState<SuspensionReasonCode | null>(
    null,
  );
  const { view, isLoading } = useSuspensions(now, regionFilter, reasonFilter);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1 }}
      >
        Trust and safety
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Suspensions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Held accounts as a worklist. Suspensions whose period has elapsed are
        due for a lift and sit on top. Pending appeals are worked in their own
        section. Lifting an account is a recorded decision the system enforces,
        never a direct change to a balance, and the reason is captured into the
        audit trail. The view spans both markets by default.
      </Typography>

      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 3 }}
        useFlexGap
        flexWrap="wrap"
      >
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="suspension-region-label">Region</InputLabel>
          <Select
            labelId="suspension-region-label"
            label="Region"
            value={regionFilter ?? 'all'}
            inputProps={{ 'data-testid': 'suspension-region' }}
            onChange={(e) =>
              setRegionFilter(
                e.target.value === 'all' ? null : (e.target.value as Market),
              )
            }
          >
            <MenuItem value="all">All markets</MenuItem>
            <MenuItem value="uk">UK</MenuItem>
            <MenuItem value="us">US</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="suspension-reason-label">Reason code</InputLabel>
          <Select
            labelId="suspension-reason-label"
            label="Reason code"
            value={reasonFilter ?? 'all'}
            inputProps={{ 'data-testid': 'suspension-reason' }}
            onChange={(e) =>
              setReasonFilter(
                e.target.value === 'all'
                  ? null
                  : (e.target.value as SuspensionReasonCode),
              )
            }
          >
            <MenuItem value="all">All reasons</MenuItem>
            {REASON_CODE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {isLoading || !view ? (
        <Loading />
      ) : (
        <>
          <Section
            title="Elapsed, pending lift"
            rows={view.elapsed}
            emptyText="No elapsed suspensions in scope."
            testId="suspension-section-elapsed"
          />
          <Divider sx={{ mb: 3 }} />
          <Section
            title="Pending appeals"
            rows={view.pendingAppeals}
            emptyText="No pending appeals in scope."
            testId="suspension-section-appeals"
          />
          <Divider sx={{ mb: 3 }} />
          <Section
            title="Running suspensions"
            rows={view.running}
            emptyText="No running suspensions in scope."
            testId="suspension-section-running"
          />
        </>
      )}
    </Box>
  );
};

/**
 * Gated entry registered at /suspensions. ROLE-gated to fraud and super_admin (like
 * the fraud queue and the audit log): the worklist is sensitive, so a viewer's
 * read-everywhere wildcard must not reach it and an editor has no business here
 * (D-suspension-flow).
 */
export const Suspensions = () => (
  <RequireAccess roles={['fraud', 'super_admin']}>
    <SuspensionsView />
  </RequireAccess>
);
